import React, { forwardRef } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { styled } from '@compiled/react';

import { Box } from '@atlaskit/primitives/compiled/box';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { token } from '@atlaskit/tokens';

import type { FeedType } from '../useFeedTypeSelector.ts';
import { useFeedTypeSelectionAnalytics } from '../useFeedTypeSelectionAnalytics.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-html-button -- Ignored via go/DSP-18766
const FeedTypePillButton = styled.button<{
	selected: boolean;
}>({
	outline: 'none',
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) =>
		props.selected ? token('color.background.selected') : token('color.background.neutral'),
	display: 'inline-flex',
	font: token('font.body'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) => (props.selected ? token('color.text.brand') : token('color.text')),
	marginTop: '0px',
	userSelect: 'none',
	maxWidth: '100%',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	verticalAlign: 'top',
	cursor: 'pointer',
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: (props) =>
			props.selected
				? token('color.background.selected.hovered')
				: token('color.background.neutral.hovered'),
	},
	padding: token('space.100'),
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: (props) =>
		props.selected
			? // TODO: Change B400 here to B700 when it becomes available
				`${token('border.width')} solid ${token('color.text.selected')}`
			: `${token('border.width')} solid ${token('color.background.neutral')}`,
	borderRadius: token('radius.full'),
	height: '32px',
	'&:focus': {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
		borderColor: 'transparent',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	display: 'flex',
	height: '100%',
	alignItems: 'center',
	marginRight: token('space.050'),
});

type FeedTypePillProps = {
	feedType: FeedType | string;
	selectedFeedType: FeedType | string;
	setFeedType: (newFeedType: FeedType | string) => void;
	Icon?: ComponentType<NewCoreIconProps>;
	children?: ReactNode;
	titleSuffix?: ReactNode;
	isPulseActive?: boolean;
	onPulseClick?: () => void;
	onSelect?: () => void;
};

export const FeedTypePill: React.ForwardRefExoticComponent<
	FeedTypePillProps & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, FeedTypePillProps>(
	(
		{
			feedType,
			selectedFeedType,
			setFeedType,
			Icon,
			children,
			titleSuffix,
			isPulseActive = false,
			onPulseClick = () => {},
			onSelect,
		},
		ref,
	) => {
		const { handleFeedTypeClick, handleMouseDown, handlePulseClick } =
			useFeedTypeSelectionAnalytics({
				feedType,
				selectedFeedType,
				setFeedType,
				isPulseActive,
				onPulseClick,
			});

		const selected = selectedFeedType === feedType;
		const testId = `${feedType.toLowerCase()}-feed-button`;

		const handleClick = () => {
			onSelect?.();
			handleFeedTypeClick();
		};

		return (
			<>
				{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable */}
				<Box padding="space.050" onClick={handlePulseClick} role="none">
					<FeedTypePillButton
						selected={selected}
						role="tab"
						id={`tab-${feedType.toLowerCase()}`}
						aria-selected={selected}
						onClick={handleClick}
						onMouseDown={handleMouseDown}
						data-testid={testId}
						ref={ref}
					>
						{Icon && (
							<IconWrapper>
								<Icon
									label=""
									color={selected ? token('color.text.selected') : token('color.text')}
								/>
							</IconWrapper>
						)}
						{children}
						{titleSuffix}
					</FeedTypePillButton>
				</Box>
			</>
		);
	},
);
